/* LessonsTab — Admin panel sub-tab for managing course content (lessons).
 * Lets admins:
 *   - approve / reject lessons submitted by instructors (queue)
 *   - publish lessons directly
 *   - browse + delete published lessons
 * Lessons are scoped per program_slug and surface to students via
 * /api/academy/my/courses/{slug} (already wired into CourseDetail.jsx). */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { BookOpen, Check, X, Trash2, Plus, RefreshCw, AlertCircle, ExternalLink } from 'lucide-react';
import FileDropField from './FileDropField';

const API = (process.env.CRA_API_BASE_URL || process.env.REACT_APP_API_BASE_URL || process.env.REACT_APP_BACKEND_URL);
const api = axios.create({ baseURL: API, withCredentials: true });

const RESOURCE_KINDS = ['link', 'video', 'pdf', 'slides', 'doc'];

function StatusPill({ status }) {
  const cfg = {
    published: { bg: 'bg-emerald-50', fg: 'text-emerald-700', border: 'border-emerald-200', label: 'Published' },
    pending:   { bg: 'bg-amber-50',   fg: 'text-amber-800',   border: 'border-amber-200',   label: 'Pending review' },
    rejected:  { bg: 'bg-red-50',     fg: 'text-red-700',     border: 'border-red-200',     label: 'Rejected' },
    draft:     { bg: 'bg-gray-50',    fg: 'text-gray-700',    border: 'border-gray-200',    label: 'Draft' },
  }[status] || { bg: 'bg-gray-50', fg: 'text-gray-700', border: 'border-gray-200', label: status };
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full border font-bold uppercase tracking-wider text-[10px] ${cfg.bg} ${cfg.fg} ${cfg.border}`}>
      {cfg.label}
    </span>
  );
}

export default function LessonsTab({ programs }) {
  const [rows, setRows] = useState([]);
  const [counts, setCounts] = useState({});
  const [filterStatus, setFilterStatus] = useState('pending');
  const [filterProgram, setFilterProgram] = useState('');
  const [loading, setLoading] = useState(false);
  const [showCreate, setShowCreate] = useState(false);
  const [reviewing, setReviewing] = useState(null);

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filterStatus) params.set('status', filterStatus);
      if (filterProgram) params.set('program_slug', filterProgram);
      const { data } = await api.get(`/api/academy/admin/lessons?${params.toString()}`);
      setRows(data.rows || []);
      setCounts(data.counts || {});
    } finally {
      setLoading(false);
    }
  }, [filterStatus, filterProgram]);

  useEffect(() => { reload(); }, [reload]);

  const approve = async (lesson) => {
    await api.patch(`/api/academy/admin/lessons/${lesson.id}`, { action: 'approve' });
    reload();
  };
  const reject = async (lesson, reason) => {
    await api.patch(`/api/academy/admin/lessons/${lesson.id}`, { action: 'reject', rejection_reason: reason });
    reload();
  };
  const remove = async (lesson) => {
    if (!window.confirm(`Delete "${lesson.title}"? Students will lose access immediately.`)) return;
    await api.delete(`/api/academy/admin/lessons/${lesson.id}`);
    reload();
  };

  const pendingCount = counts.pending || 0;

  return (
    <div data-testid="academy-admin-lessons-tab">
      {/* Header: stats + filters + new */}
      <div className="bg-white rounded-2xl border border-[#E5E5E7] p-5 mb-5 flex flex-wrap items-center gap-4">
        <div className="flex items-center gap-2">
          <BookOpen className="w-4 h-4 text-[#c9a227]" />
          <span className="text-[13px] font-bold tracking-[0.18em] uppercase text-[#0e0e10]">Course Content</span>
        </div>
        <span data-testid="lessons-pending-count"
          className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-bold uppercase tracking-wider border
          ${pendingCount > 0 ? 'bg-amber-50 text-amber-800 border-amber-200' : 'bg-gray-50 text-gray-500 border-gray-200'}`}>
          {pendingCount > 0 && <AlertCircle className="w-3 h-3" />}
          {pendingCount} pending
        </span>
        <span className="text-[12px] text-[#6E6E73]">
          {(counts.published || 0)} published · {(counts.rejected || 0)} rejected
        </span>

        <div className="ml-auto flex items-center gap-2">
          <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)}
            data-testid="lessons-filter-status"
            className="text-[12px] rounded-full border border-[#E5E5E7] px-3 py-1.5 outline-none focus:border-[#c9a227]">
            <option value="">All statuses</option>
            <option value="pending">Pending</option>
            <option value="published">Published</option>
            <option value="rejected">Rejected</option>
          </select>
          <select value={filterProgram} onChange={(e) => setFilterProgram(e.target.value)}
            data-testid="lessons-filter-program"
            className="text-[12px] rounded-full border border-[#E5E5E7] px-3 py-1.5 outline-none focus:border-[#c9a227] max-w-[180px]">
            <option value="">All programs</option>
            {(programs || []).map((p) => (
              <option key={p.slug} value={p.slug}>{p.name}</option>
            ))}
          </select>
          <button onClick={reload} className="inline-flex items-center gap-1 text-[12px] text-[#6E6E73] hover:text-[#c9a227]" data-testid="lessons-refresh">
            <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} /> Refresh
          </button>
          <button onClick={() => setShowCreate(true)}
            data-testid="lessons-new-btn"
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[12px] font-bold uppercase tracking-wider text-[#0e0e10]"
            style={{ background: 'linear-gradient(90deg,#e6c35a 0%,#c9a227 100%)' }}>
            <Plus className="w-3.5 h-3.5" /> New lesson
          </button>
        </div>
      </div>

      {/* Lessons list */}
      {rows.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-[#E5E5E7] bg-white p-10 text-center text-[#6E6E73]">
          <BookOpen className="w-10 h-10 text-[#c9a227] mx-auto mb-3" />
          <div className="text-[14px] font-bold text-[#1D1D1F] mb-1">No lessons in this view</div>
          <p className="text-[12.5px]">{filterStatus === 'pending' ? 'No instructor submissions awaiting review.' : 'Try a different filter, or click "New lesson" to publish content directly.'}</p>
        </div>
      ) : (
        <ul className="space-y-3">
          {rows.map((l) => (
            <LessonRow key={l.id} lesson={l}
              programs={programs}
              onApprove={() => approve(l)}
              onReject={(reason) => reject(l, reason)}
              onDelete={() => remove(l)}
              onEdit={() => setReviewing(l)} />
          ))}
        </ul>
      )}

      {showCreate && (
        <LessonFormModal programs={programs} onClose={() => setShowCreate(false)}
          onSaved={() => { setShowCreate(false); reload(); }} />
      )}
      {reviewing && (
        <LessonFormModal lesson={reviewing} programs={programs}
          onClose={() => setReviewing(null)}
          onSaved={() => { setReviewing(null); reload(); }} />
      )}
    </div>
  );
}

function LessonRow({ lesson, programs, onApprove, onReject, onDelete, onEdit }) {
  const [showRejectBox, setShowRejectBox] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  const programName = useMemo(() => {
    const p = (programs || []).find((x) => x.slug === lesson.program_slug);
    return p?.name || lesson.program_slug;
  }, [programs, lesson.program_slug]);

  return (
    <li className="rounded-2xl border border-[#E5E5E7] bg-white p-5" data-testid={`lesson-row-${lesson.id}`}>
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="flex-1 min-w-[260px]">
          <div className="flex items-center gap-2 flex-wrap mb-1">
            <h3 className="text-[15px] font-extrabold text-[#0e0e10]">{lesson.title}</h3>
            <StatusPill status={lesson.status} />
          </div>
          <div className="text-[11.5px] text-[#6E6E73] mb-2">
            <span className="font-semibold text-[#0e0e10]">{programName}</span>
            {lesson.module_title ? ` · ${lesson.module_title}` : ''}
            {' · by '}
            <span className="font-mono">{lesson.created_by_email}</span>
            {' · '}
            {new Date(lesson.created_at).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' })}
          </div>
          {lesson.body_md && (
            <p className="text-[13px] text-[#3a3a3f] whitespace-pre-wrap line-clamp-3 mb-2">{lesson.body_md}</p>
          )}
          {(lesson.resources || []).length > 0 && (
            <div className="flex flex-wrap gap-1.5">
              {lesson.resources.map((r, i) => (
                <a key={i} href={r.url} target="_blank" rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-[#f8f8fa] border border-[#E5E5E7] text-[11px] text-[#0e0e10] hover:border-[#c9a227]">
                  <ExternalLink className="w-3 h-3" /> {r.label} <span className="text-[#6E6E73]">· {r.kind}</span>
                </a>
              ))}
            </div>
          )}
          {lesson.status === 'rejected' && lesson.rejection_reason && (
            <div className="mt-2 rounded-lg bg-red-50 border border-red-200 px-3 py-2 text-[12.5px] text-red-700">
              <strong>Rejection reason:</strong> {lesson.rejection_reason}
            </div>
          )}
        </div>

        <div className="flex flex-col gap-1.5 items-end">
          {lesson.status === 'pending' && (
            <>
              <button onClick={onApprove}
                data-testid={`lesson-approve-${lesson.id}`}
                className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-[11.5px] font-bold uppercase tracking-wider bg-emerald-600 text-white hover:bg-emerald-700">
                <Check className="w-3.5 h-3.5" /> Approve
              </button>
              <button onClick={() => setShowRejectBox((v) => !v)}
                data-testid={`lesson-reject-toggle-${lesson.id}`}
                className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-[11.5px] font-bold uppercase tracking-wider bg-white border border-red-200 text-red-700 hover:bg-red-50">
                <X className="w-3.5 h-3.5" /> Reject
              </button>
            </>
          )}
          <button onClick={onEdit}
            data-testid={`lesson-edit-${lesson.id}`}
            className="text-[11.5px] text-[#c9a227] hover:underline font-semibold">Edit</button>
          <button onClick={onDelete}
            data-testid={`lesson-delete-${lesson.id}`}
            className="inline-flex items-center gap-1 text-[11.5px] text-red-600 hover:underline">
            <Trash2 className="w-3 h-3" /> Delete
          </button>
        </div>
      </div>

      {showRejectBox && (
        <div className="mt-3 flex items-stretch gap-2" data-testid={`lesson-reject-box-${lesson.id}`}>
          <input value={rejectReason} onChange={(e) => setRejectReason(e.target.value)}
            placeholder="Reason (visible to the instructor)"
            data-testid={`lesson-reject-reason-${lesson.id}`}
            className="flex-1 px-3 py-2 rounded-lg border border-[#E5E5E7] outline-none focus:border-[#c9a227] text-[13px]" />
          <button onClick={() => { onReject(rejectReason); setShowRejectBox(false); setRejectReason(''); }}
            disabled={!rejectReason.trim()}
            data-testid={`lesson-reject-confirm-${lesson.id}`}
            className="px-4 rounded-lg bg-red-600 text-white text-[12.5px] font-bold uppercase tracking-wider disabled:opacity-50">
            Send
          </button>
        </div>
      )}
    </li>
  );
}

function LessonFormModal({ lesson, programs, onClose, onSaved }) {
  const editing = !!lesson;
  const [programSlug, setProgramSlug] = useState(lesson?.program_slug || (programs?.[0]?.slug ?? ''));
  const [moduleTitle, setModuleTitle] = useState(lesson?.module_title || '');
  const [title, setTitle] = useState(lesson?.title || '');
  const [body, setBody] = useState(lesson?.body_md || '');
  const [resources, setResources] = useState(lesson?.resources || []);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const addRes = () => setResources((r) => [...r, { label: '', url: '', kind: 'link' }]);
  const updateRes = (i, patch) => setResources((r) => r.map((row, j) => j === i ? { ...row, ...patch } : row));
  const removeRes = (i) => setResources((r) => r.filter((_, j) => j !== i));

  const submit = async (e) => {
    e.preventDefault();
    if (!title.trim() || !programSlug) { setError('Title and program are required'); return; }
    setBusy(true); setError('');
    try {
      const body_payload = {
        program_slug: programSlug,
        module_title: moduleTitle.trim() || null,
        title: title.trim(),
        body_md: body,
        resources: resources.filter((r) => r.url.trim()),
      };
      if (editing) {
        await api.patch(`/api/academy/admin/lessons/${lesson.id}`, body_payload);
      } else {
        await api.post(`/api/academy/admin/lessons`, body_payload);
      }
      onSaved();
    } catch (err) {
      setError(err.response?.data?.detail || err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/70 z-[100] flex items-start justify-center px-4 py-8 overflow-auto" data-testid="lesson-form-modal">
      <div className="bg-white rounded-2xl shadow-2xl max-w-[640px] w-full overflow-hidden">
        <div className="px-6 py-4 flex items-center justify-between"
          style={{ background: 'linear-gradient(135deg,#c9a227 0%,#a8871a 100%)' }}>
          <div className="text-white">
            <div className="text-[10px] font-bold tracking-[0.22em] uppercase text-white/80">{editing ? 'Edit lesson' : 'New lesson'}</div>
            <div className="text-[16px] font-extrabold">{editing ? lesson.title : 'Publish course content'}</div>
          </div>
          <button onClick={onClose} className="text-white/80 hover:text-white" data-testid="lesson-form-close">
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={submit} className="px-6 py-5 space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-[10px] font-bold tracking-[0.18em] uppercase text-[#6E6E73] mb-1">Program</label>
              <select value={programSlug} onChange={(e) => setProgramSlug(e.target.value)}
                disabled={editing}
                data-testid="lesson-form-program"
                className="w-full px-3 py-2 rounded-lg border border-[#E5E5E7] focus:border-[#c9a227] outline-none text-[13.5px] disabled:bg-[#f5f5f7]">
                {(programs || []).map((p) => (
                  <option key={p.slug} value={p.slug}>{p.name}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-[10px] font-bold tracking-[0.18em] uppercase text-[#6E6E73] mb-1">Module (optional)</label>
              <input value={moduleTitle} onChange={(e) => setModuleTitle(e.target.value)}
                data-testid="lesson-form-module"
                placeholder="Module 1: Foundations"
                className="w-full px-3 py-2 rounded-lg border border-[#E5E5E7] focus:border-[#c9a227] outline-none text-[13.5px]" />
            </div>
          </div>

          <div>
            <label className="block text-[10px] font-bold tracking-[0.18em] uppercase text-[#6E6E73] mb-1">Title</label>
            <input value={title} onChange={(e) => setTitle(e.target.value)} required
              data-testid="lesson-form-title"
              className="w-full px-3 py-2 rounded-lg border border-[#E5E5E7] focus:border-[#c9a227] outline-none text-[14px]" />
          </div>

          <div>
            <label className="block text-[10px] font-bold tracking-[0.18em] uppercase text-[#6E6E73] mb-1">Lesson body (Markdown OK)</label>
            <textarea value={body} onChange={(e) => setBody(e.target.value)} rows={10}
              data-testid="lesson-form-body"
              className="w-full px-3 py-2 rounded-lg border border-[#E5E5E7] focus:border-[#c9a227] outline-none text-[13.5px] font-mono" />
          </div>

          <div>
            <div className="flex items-center justify-between mb-1.5">
              <label className="text-[10px] font-bold tracking-[0.18em] uppercase text-[#6E6E73]">Resources</label>
              <button type="button" onClick={addRes}
                data-testid="lesson-form-add-resource"
                className="inline-flex items-center gap-1 text-[11.5px] text-[#c9a227] hover:underline font-semibold">
                <Plus className="w-3 h-3" /> Add link
              </button>
            </div>
            <FileDropField
              onUploaded={(res) => setResources((rs) => [...rs, { label: res.label, url: res.url, kind: res.kind }])}
            />
            <div className="space-y-1.5 mt-2">
              {resources.map((r, i) => (
                <div key={i} className="flex items-stretch gap-2">
                  <input value={r.label} onChange={(e) => updateRes(i, { label: e.target.value })}
                    placeholder="Label"
                    className="w-[150px] px-2 py-1.5 rounded-lg border border-[#E5E5E7] outline-none focus:border-[#c9a227] text-[12.5px]" />
                  <input value={r.url} onChange={(e) => updateRes(i, { url: e.target.value })}
                    placeholder="https://…"
                    className="flex-1 px-2 py-1.5 rounded-lg border border-[#E5E5E7] outline-none focus:border-[#c9a227] text-[12.5px]" />
                  <select value={r.kind} onChange={(e) => updateRes(i, { kind: e.target.value })}
                    className="px-2 py-1.5 rounded-lg border border-[#E5E5E7] outline-none focus:border-[#c9a227] text-[12.5px]">
                    {RESOURCE_KINDS.map((k) => <option key={k} value={k}>{k}</option>)}
                  </select>
                  <button type="button" onClick={() => removeRes(i)} className="text-red-600 hover:bg-red-50 rounded-lg px-2">
                    <X className="w-4 h-4" />
                  </button>
                </div>
              ))}
            </div>
          </div>

          {error && (
            <div className="rounded-lg bg-red-50 border border-red-200 text-red-700 text-[12.5px] px-3 py-2" data-testid="lesson-form-error">
              {error}
            </div>
          )}

          <div className="flex items-center justify-end gap-2 pt-2">
            <button type="button" onClick={onClose} className="text-[12.5px] text-[#6E6E73] hover:text-[#0e0e10] px-3">Cancel</button>
            <button type="submit" disabled={busy}
              data-testid="lesson-form-save"
              className="inline-flex items-center gap-1.5 px-5 py-2 rounded-full text-[12.5px] font-bold uppercase tracking-wider text-[#0e0e10] disabled:opacity-60"
              style={{ background: 'linear-gradient(90deg,#e6c35a 0%,#c9a227 100%)' }}>
              {busy ? 'Saving…' : (editing ? 'Save changes' : 'Publish lesson')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
